import React, { useState } from 'react';
import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';

import {
	IconLock,
	IconChevronUpThick,
	IconCheckSmall,
	IconChevronDownThick,
	IconPlaySmall,
} from '@sololearnorg/sol';

import { Container } from '../../../../../symphony';
import {
	ActionType,
	RequestType,
	IMaterialProps,
} from '../../../global-interface';
import { SlLEContext } from '../../../global-constants';
import { SlActions } from '../../../services/ActionsService/sl-actions';
import { SlDataService } from '../../../services/sl-data.service';
import { ActionOnMountWrapper } from '../../Wrappers/ActionOnMountWrapper';
import { useContainerData } from '../../../../../shared/public/SlHooks/useContainerData';
import { VisibilityStatus } from '../../../../../api/private/learn-engine-api/learn-engine-api.interface';

import s from './Module.scss';

export function Module({
	materialInfo: {
		header: { title, id, materialRelationId },
		status: { visibility, isCompleted },
		childrenHeight,
	},
	children,
	isExpanded = true,
}: IMaterialProps): JSX.Element {
	useStyles(s);
	const [expanded, setExpanded] = useState<boolean>(isExpanded);

	const { currentItems$: currentItems, isCourseAvailable$: isCourseAvailable } = useContainerData(
		SlLEContext,
		SlDataService,
		['currentItems$', 'isCourseAvailable$'],
	);

	const iconRenderer = (iconType: 'status' | 'state'): React.ReactNode => {
		if (iconType === 'status') {
			if (isCompleted) {
				return (
					<div className="le-module__state-wrapper le-module__state-wrapper--complete">
						<IconCheckSmall
							size={16}
							className="le-module__state-icon le-module__state-icon--complete"
						/>
					</div>
				);
			}
			if (visibility === VisibilityStatus.Locked || !isCourseAvailable) {
				return (
					<div className="le-module__state-wrapper le-module__state-wrapper--locked">
						<IconLock
							size={16}
							className="le-module__state-icon le-module__state-icon--locked"
						/>
					</div>
				);
			}
			return (
				<div className="le-module__state-wrapper le-module__state-wrapper--started">
					<IconPlaySmall
						size={16}
						className="le-module__state-icon le-module__state-icon--started"
					/>
				</div>
			);
		}

		if (iconType === 'state') {
			if (!isCourseAvailable) {
				return null;
			}
			if (expanded) {
				return (
					<IconChevronUpThick
						size={20}
						className="le-module__dropdown-icon"
					/>
				);
			}
			return (
				<IconChevronDownThick
					size={20}
					className="le-module__dropdown-icon"
				/>
			);
		}
		return null;
	};

	const onModuleClick = () => {
		if (isCourseAvailable) {
			const { actions$ } = Container.take(SlLEContext, SlActions);
			actions$.next({
				type: ActionType.OnModuleClick,
				requestType: RequestType.Parallel,
				data: {
					id: materialRelationId,
				},
			});
			setExpanded(!expanded);
		}
	};

	const isCurrentModule = currentItems.some(
		({ moduleId, isDefault }) => moduleId === materialRelationId && isDefault,
	);

	return (
		<>
			<div
				onClick={onModuleClick}
				className={classNames('le-module', {
					blocked: !isCourseAvailable,
					expanded: expanded && isCourseAvailable,
				})}
			>
				{iconRenderer('status')}
				<h4 className="le-module__title">
					{title}
				</h4>
				{iconRenderer('state')}
			</div>
			<div
				style={{
					maxHeight: expanded
						? `${childrenHeight ? `${childrenHeight}px` : 'unset'}`
						: '0',
				}}
				className={classNames('le-module__children', {
					collapsed: !expanded,
				})}
			>
				{isCurrentModule ? (
					<ActionOnMountWrapper
						id={id}
						action={{
							type: ActionType.OnCurrentModuleChildrenMount,
							requestType: RequestType.Parallel,
							data: {
								id: materialRelationId,
							},
						}}
					>
						{children}
					</ActionOnMountWrapper>
				) : (
					children
				)}
			</div>
		</>
	);
}
